/**
 * The canonical segmented control — log tabs, log levels, metric ranges.
 * An 8px rail holding pills; the active pill lifts on the one xs shadow.
 * Replaces the loose chips those filters used to be.
 */
export interface Segment<T extends string> {
  value: T,
  label: React.ReactNode,
}

interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[],
  value: T,
  onChange: (value: T) => void,
  /** 24px on desktop, 32px where the control is a touch target. */
  size?: 'sm' | 'touch',
  ariaLabel?: string,
  className?: string,
}

const SegmentedControl = <T extends string>({
  segments,
  value,
  onChange,
  size = 'sm',
  ariaLabel,
  className = '',
}: SegmentedControlProps<T>) => (
  <div role="tablist" aria-label={ariaLabel} className={`b4-segmented ${className}`}>
    {segments.map(segment => {
      const isActive = segment.value === value;
      return (
        <button
          key={segment.value}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(segment.value)}
          className={[
            'b4-focus font-inter text-ds-125 font-medium rounded-[4px] cursor-pointer',
            'transition-colors duration-[120ms]',
            size === 'sm' ? 'h-6 px-[11px]' : 'h-8 px-3',
            isActive
              ? 'bg-navy-800 text-white shadow-[0_1px_2px_rgba(0,0,0,.4)]'
              : 'bg-transparent text-navy-200 hover:text-white',
          ].join(' ')}
        >
          {segment.label}
        </button>
      );
    })}
  </div>
)

export default SegmentedControl
